import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT, IMAGES } from "@/data/content";

const NAV_LINKS = [
  { label: "Home", href: "#top", testid: "nav-home" },
  { label: "Residential Cleaning", href: "#residential", testid: "nav-residential" },
  { label: "Deep Cleaning", href: "#deep-cleaning", testid: "nav-deep-cleaning" },
  { label: "Move-In / Move-Out", href: "#move-cleaning", testid: "nav-move-cleaning" },
  { label: "Commercial Cleaning", href: "#commercial", testid: "nav-commercial" },
  { label: "Logistics Services", href: "#logistics", testid: "nav-logistics" },
  { label: "About Us", href: "#about-us", testid: "nav-about-us" },
  { label: "Our Work", href: "/our-work", testid: "nav-our-work" },
];

const menuItem = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    if (pathname !== "/") {
      navigate("/" + href);
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const linkCls =
    "text-xs font-medium text-zinc-300 hover:text-gold transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-gold/60 rounded-sm px-1 py-0.5 whitespace-nowrap";

  return (
    <>
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled || open ? "bg-black/80 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-4 md:px-6 flex items-center justify-between h-16 md:h-20 gap-4">
        <a
          href="#top"
          data-testid="logo-link"
          onClick={(e) => { e.preventDefault(); go("#top"); }}
          className="flex items-center gap-3 leading-none shrink-0"
        >
          <img
            src={IMAGES.logo}
            alt="Perfect Touch logo"
            className="h-9 w-9 md:h-11 md:w-11 rounded-full object-cover border border-gold/40"
          />
          <span className="flex flex-col">
            <span className="font-display font-bold text-lg md:text-xl tracking-tight text-white whitespace-nowrap">
              Perfect Touch
            </span>
            <span className="text-[8px] md:text-[10px] tracking-[0.28em] text-gold uppercase mt-0.5 whitespace-nowrap hidden min-[1400px]:block">
              Detailed Cleaning &amp; Logistics
            </span>
          </span>
        </a>

        <nav className="hidden xl:flex items-center gap-2 min-[1400px]:gap-3.5" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <button key={l.href} data-testid={l.testid} onClick={() => go(l.href)} className={linkCls}>
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-4 shrink-0">
          <a
            href={CONTACT.phoneHref}
            data-testid="header-phone"
            className="hidden lg:flex items-center gap-2 text-[13px] font-medium text-zinc-300 hover:text-gold transition-colors duration-300 whitespace-nowrap"
          >
            <span className="w-8 h-8 flex items-center justify-center rounded-full border border-gold/40 text-gold">
              <Phone size={13} />
            </span>
            {CONTACT.phone}
          </a>
          <button
            data-testid="header-quote-btn"
            onClick={() => go("#quote")}
            className="hidden sm:inline-block rounded-full bg-gold text-black text-[13px] font-semibold px-5 py-2.5 hover:bg-[#c5a059] transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-gold/60 whitespace-nowrap"
          >
            Get a Quote
          </button>
          <a
            href={CONTACT.phoneHref}
            data-testid="mobile-call-btn"
            aria-label={`Call Perfect Touch at ${CONTACT.phone}`}
            className="lg:hidden w-9 h-9 flex items-center justify-center rounded-full border border-gold/40 text-gold hover:bg-gold hover:text-black transition-colors duration-300"
          >
            <Phone size={16} />
          </a>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden text-white p-1 focus:outline-none focus:ring-2 focus:ring-gold/60 rounded-sm"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
    </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="xl:hidden fixed inset-x-0 top-16 md:top-20 bottom-0 z-40 bg-black/95 backdrop-blur-xl overflow-y-auto"
            data-testid="mobile-menu"
          >
            <motion.nav
              initial="hidden"
              animate="show"
              variants={{ show: { transition: { staggerChildren: 0.05, delayChildren: 0.1 } } }}
              className="px-6 py-8 flex flex-col"
              aria-label="Mobile"
            >
              {NAV_LINKS.map((l) => (
                <motion.button
                  key={l.href}
                  variants={menuItem}
                  data-testid={`mobile-${l.testid}`}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-2xl text-white py-2.5 border-l border-transparent pl-4 hover:border-gold hover:text-gold transition-colors duration-300"
                >
                  {l.label}
                </motion.button>
              ))}

              <motion.div variants={menuItem} className="mt-7 pt-7 border-t border-white/10 flex flex-col gap-4">
                <a
                  href={CONTACT.phoneHref}
                  data-testid="mobile-phone"
                  className="flex items-center gap-3 text-lg text-zinc-200 hover:text-gold transition-colors duration-300"
                >
                  <span className="w-9 h-9 flex items-center justify-center rounded-full border border-gold/40 text-gold">
                    <Phone size={15} />
                  </span>
                  {CONTACT.phone}
                </a>
                <button
                  data-testid="mobile-quote-btn"
                  onClick={() => go("#quote")}
                  className="rounded-full bg-gold text-black font-semibold px-6 py-4 text-center hover:bg-[#c5a059] transition-colors duration-300"
                >
                  Get a Quote
                </button>
              </motion.div>
            </motion.nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
